/**
 * The notification layer's wiring (main.tsx).
 *
 * - startNotifyWiring (notification settings, 2026-10-29): the rules follow
 *   the Console's `notifications` flag; a timed mute ends on time (the
 *   screens redraw when the next one runs out); a server joined while
 *   connected gets its saved settings (READY carries the others).
 * - connectNotifications (OS notifications, 2026-11-01): the unread badge
 *   follows the data, a click on a desktop toast opens its route, and
 *   looking at a conversation takes its toasts away.
 */
import { nextMuteEnd, type GatewayDispatch } from '@neosocial/protocol';
import { flagOn, onConfigChange } from '../store/config';
import { updateData, useData } from '../store/data';
import { loadGuildNotify } from './actions';
import { setNotifyFlagProbe } from './rules';
import { desktopBridge } from '../lib/config';
import { useUi } from '../store/ui';
import { useUm } from '../store/um';
import { usePanes } from '../shell/panes';
import { appIconPng, badgeRgba, renderFavicon, unreadBadgeCount } from './badge';
import { seenConversation } from './notify';
import { useNotifyPrefs } from './prefs';
import { decodeRoute, openRoute, shownKey, windowFocused } from './route';
import { setTitleCount } from './title';

let off: (() => void) | null = null;

/** Redraws everything that reads the settings (they hold the same values). */
function bump(): void {
  updateData((s) => ({ ...s, guildSettings: { ...s.guildSettings } }));
}

/** Called once at start; again re-binds. */
export function startNotifyWiring(): void {
  off?.();
  setNotifyFlagProbe(() => flagOn('notifications'));
  let timer: ReturnType<typeof setTimeout> | undefined;
  const schedule = () => {
    clearTimeout(timer);
    const now = Date.now();
    let next: number | null = null;
    for (const g of Object.values(useData.getState().guildSettings)) {
      const end = nextMuteEnd(g, now);
      if (end !== null && (next === null || end < next)) next = end;
    }
    // a little after the end, so the mute reads as over; at most ~24 days ahead (setTimeout)
    if (next !== null) timer = setTimeout(bump, Math.min(next - now + 50, 0x7fffffff));
  };
  const offData = useData.subscribe((st, prev) => {
    if (st.guildSettings !== prev.guildSettings) schedule();
  });
  const offConfig = onConfigChange((next, prev) => {
    if (flagOn('notifications', next) !== flagOn('notifications', prev)) bump();
  });
  schedule();
  off = () => {
    clearTimeout(timer);
    offData();
    offConfig();
  };
}

/** The gateway glue (store/session.ts). */
export function onNotifyDispatch(frame: GatewayDispatch): void {
  if (frame.t === 'GUILD_CREATE' && !useData.getState().guildSettings[frame.d.id])
    void loadGuildNotify(frame.d.id);
}

/* ------------------------------------------------ the OS notifications */

/** The badge's size on the taskbar (Windows scales it to 16 to 32 px). */
const OVERLAY = 32;

let started = false;

export function connectNotifications(): void {
  if (started) return;
  started = true;
  const bridge = desktopBridge();

  // the badge: at most four times a second, and only when the number changes
  let shown = -1;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const update = () => {
    timer = null;
    const n = useNotifyPrefs.getState().prefs.badge
      ? unreadBadgeCount(useData.getState(), useUm.getState().ignored)
      : 0;
    if (n === shown) return;
    shown = n;
    setTitleCount(n);
    if (!bridge) {
      void renderFavicon(n).catch(() => {});
      return;
    }
    const rgba = n > 0 ? badgeRgba(n, OVERLAY) : null;
    void bridge.setBadge?.(n, rgba ? { rgba, size: OVERLAY } : null).catch(() => {});
  };
  const later = () => {
    timer ??= setTimeout(update, 250);
  };
  useData.subscribe(later);
  useUm.subscribe(later);
  useNotifyPrefs.subscribe(later);
  update();

  // the desktop app's toasts: the shell has shown the window already
  bridge?.onNotificationClick?.((route) => openRoute(decodeRoute(route)));
  // the web's toasts carry the app's icon as a PNG: draw it once, ahead of the first
  if (!bridge) appIconPng();

  // what you look at (with the focus) loses its toasts
  const seen = () => {
    if (!windowFocused()) return;
    const k = shownKey();
    if (k) seenConversation(k);
  };
  useUi.subscribe(seen);
  usePanes.subscribe(seen);
  if (typeof window !== 'undefined') window.addEventListener('focus', seen);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', seen);
}
